import { useRef } from 'react';

import Card from '@perpay-web/components/base/Card/Card';
import CustomButton from '@perpay-web/components/base/CustomButton/CustomButton';
import { useModal } from '@perpay-web/components/base/Modal/useModal';
import WarningLoaderCircle from '@perpay-web/components/base/WarningLoaderCircle/WarningLoaderCircle';
import LegacyFocusLayout from '@perpay-web/components/composite/LegacyFocusLayout/LegacyFocusLayout';
import CardCta from '@perpay-web/fintech/components/base/CardCta/CardCta';
import CustomLink from '@perpay-web/fintech/components/base/CustomLink/CustomLink';
import ResponsiveCopy from '@perpay-web/fintech/components/base/ResponsiveCopy/ResponsiveCopy';
import ResponsiveHeader from '@perpay-web/fintech/components/base/ResponsiveHeader/ResponsiveHeader';
import CopyIconButton from '@perpay-web/fintech/components/composite/CopyIconButton/CopyIconButton';
import PayrollInstructionsModalContainer from '@perpay-web/fintech/components/containers/PayrollInstructionsModalContainer';
import { paths } from '@perpay-web/fintech/props/appPaths';
import { useMountAndUnmount } from '@perpay-web/hooks/useMountAndUnmount';
import { formatCurrency } from '@perpay-web/utils/stringUtils';

import styles from './CardPinwheelSetupFailure.scss';

const getInnerText = (element) => element.innerText;

const CardPinwheelSetupFailure = ({
    isLoading,
    onClickTryAgain,
    onClickDone,
    accountNumber,
    routingNumber,
    minimumPayment,
    amountToCharge = 0,
    directDepositFormUrl,
    onClickForm,
    onClickInstructions,
    onMount,
    onUnmount,
    onModalClose,
    isPHO = false,
}) => {
    useMountAndUnmount(() => {
        onMount();
        return onUnmount;
    });

    const { isModalOpen, setIsModalOpen } = useModal();

    const routingNumberRef = useRef(null);
    const accountNumberRef = useRef(null);
    const amountRef = useRef(null);
    const amount = amountToCharge || minimumPayment;

    if (isLoading) {
        return <WarningLoaderCircle />;
    }

    return (
        <LegacyFocusLayout
            className={styles.container}
            footer={
                <div className={styles['footer-buttons']}>
                    <CardCta
                        Component={CustomLink}
                        mode='dark-inverse'
                        className={styles['done-button']}
                        onClick={onClickDone}
                        href={`${paths.dashboard.path}${isPHO ? '?PHO_complete=true' : ''}`}
                    >
                        Done
                    </CardCta>
                    <CardCta
                        Component={CustomButton}
                        className={styles['try-again-button']}
                        onClick={onClickTryAgain}
                    >
                        Try Again
                    </CardCta>
                </div>
            }
        >
            <ResponsiveHeader>Direct deposit details</ResponsiveHeader>
            <ResponsiveCopy>
                We were unable to set up your direct deposit. Tap try again to
                set up direct deposit automatically or provide the information
                below to your employer&apos;s payroll team.
            </ResponsiveCopy>
            <Card className={styles.card}>
                <div>
                    <div className={styles.item}>
                        <div className={styles['item-header']}>
                            Routing Number
                        </div>
                        <div className={styles['item-content']}>
                            <span ref={routingNumberRef} id='routing-number'>
                                {routingNumber}
                            </span>
                            <CopyIconButton
                                textElementRef={routingNumberRef}
                                valueGetter={getInnerText}
                            />
                        </div>
                    </div>
                    <div className={styles.item}>
                        <div className={styles['item-header']}>
                            Account Number
                        </div>
                        <div className={styles['item-content']}>
                            <span ref={accountNumberRef} id='account-number'>
                                {accountNumber}
                            </span>
                            <CopyIconButton
                                textElementRef={accountNumberRef}
                                valueGetter={getInnerText}
                            />
                        </div>
                    </div>
                    <div className={styles.item}>
                        <div className={styles['item-header']}>Amount</div>
                        <div className={styles['item-content']}>
                            <span ref={amountRef} id='amount'>
                                {formatCurrency(amount)}
                            </span>
                            <CopyIconButton
                                textElementRef={amountRef}
                                valueGetter={getInnerText}
                            />
                        </div>
                    </div>
                    <div>
                        <div className={styles['item-header']}>
                            Account Type
                        </div>
                        <div className={styles['item-content']}>Checking</div>
                    </div>
                </div>
            </Card>
            <div className={styles.help}>Other options</div>
            <ul className={styles.list}>
                <CustomLink
                    action='inline-link'
                    onClick={onClickForm}
                    href={directDepositFormUrl}
                    target='_blank'
                >
                    <li className={styles['list-item']}>
                        Provide a form for your payroll department
                    </li>
                </CustomLink>
                <CustomButton
                    action='inline-link'
                    onClick={() => {
                        onClickInstructions();
                        setIsModalOpen(true);
                    }}
                >
                    <li>Need some help? Tap here for instructions</li>
                </CustomButton>
            </ul>

            {isModalOpen ? (
                <PayrollInstructionsModalContainer
                    onClose={() => {
                        setIsModalOpen(false);
                        onModalClose();
                    }}
                    customAmount={amount}
                />
            ) : null}
        </LegacyFocusLayout>
    );
};

export default CardPinwheelSetupFailure;
